/**
 * Shared types for the Test Cases Module v2.
 * Mirrors the backend ITestCase / ITestFolder schemas.
 */

export type TestType     = 'MANUAL' | 'AUTOMATED';
export type TestSeverity = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
export type TestCaseType = 'FUNCTIONAL' | 'REGRESSION' | 'SMOKE' | 'PERFORMANCE' | 'SECURITY' | 'USABILITY';
export type TestStatus   = 'PASSED' | 'FAILED' | 'SKIPPED' | 'PENDING' | 'RUNNING' | 'ERROR';

export interface ITestStep {
    id: string;
    action: string;
    expectedResult: string;
    status: TestStatus;
    comment?: string;
    attachmentUrl?: string;
}

/** Full test case document as returned by the API. */
export interface ITestCaseRow {
    _id: string;
    organizationId: string;
    projectId: string;
    title: string;
    description?: string;
    suite?: string;
    preconditions?: string;
    type: TestType;
    steps?: ITestStep[];
    // v2 fields
    tcNumber?: number;
    tcId?: string;
    folderId?: string;
    folderPath?: string;
    sortOrder?: number;
    severity?: TestSeverity;
    caseType?: TestCaseType;
    tags?: string[];
    createdBy?: string;
    // stability
    stabilityScore?: string;
    averageDurationMs?: number;
    isQuarantined?: boolean;
    retryAbuseCount?: number;
    createdAt: string;
    updatedAt: string;
}

/** Folder document as returned by GET /api/test-case-folders. */
export interface ITestFolder {
    _id: string;
    organizationId: string;
    projectId: string;
    name: string;
    parentId?: string;
    path: string;
    order: number;
    createdBy: string;
    createdAt: string;
    updatedAt: string;
}

/** Recursive tree node built from the flat folder list. */
export interface IFolderNode extends ITestFolder {
    children: IFolderNode[];
    depth: number;
}

export const SEVERITY_LABELS: Record<TestSeverity, string> = {
    CRITICAL: 'Critical',
    HIGH:     'High',
    MEDIUM:   'Medium',
    LOW:      'Low',
};

export const CASE_TYPE_LABELS: Record<TestCaseType, string> = {
    FUNCTIONAL:  'Functional',
    REGRESSION:  'Regression',
    SMOKE:       'Smoke',
    PERFORMANCE: 'Performance',
    SECURITY:    'Security',
    USABILITY:   'Usability',
};

export const ALL_SEVERITIES: TestSeverity[]  = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];
export const ALL_CASE_TYPES: TestCaseType[]  = ['FUNCTIONAL', 'REGRESSION', 'SMOKE', 'PERFORMANCE', 'SECURITY', 'USABILITY'];

/** Sentinel value used in the store when the detail pane is in "create new" mode. */
export const NEW_TEST_CASE_ID = '__new__';
